import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

async function renderSectionManually(sectionId, selector = null, preserveSelectors = []) {
  const fullSectionId = `shopify-section-${sectionId}`;
  const existingSection = document.getElementById(fullSectionId);

  if (!existingSection) {
    console.warn(`[manualSectionRenderer] No section found in DOM with ID ${fullSectionId}`);
    return;
  }

  // 1. Store preserved elements
  const preservedElements = new Map();
  for (const sel of preserveSelectors) {
    const el = existingSection.querySelector(sel);
    if (el) {
      preservedElements.set(sel, el.cloneNode(true));
    } else {
      console.warn(`[manualSectionRenderer] Selector "${sel}" not found for preservation`);
    }
  }

  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);

  try {
    const response = await fetch(url.toString());
    const htmlText = await response.text();
    const newDOM = new DOMParser().parseFromString(htmlText, 'text/html');

    const fetchedSection = newDOM.getElementById(fullSectionId);
    if (!fetchedSection) {
      throw new Error(`Section ID ${fullSectionId} not found in fetched HTML`);
    }

    let contentToInject = fetchedSection;

    // 2. Narrow down to the inner selector if specified
    if (selector) {
      const selectedElement = fetchedSection.querySelector(selector);
      if (!selectedElement) {
        throw new Error(`Selector "${selector}" not found in section ${sectionId}`);
      }
      contentToInject = selectedElement;
    }

    // 3. Replace section content
    existingSection.innerHTML = contentToInject.innerHTML;

    // 4. Re-insert preserved elements back into the correct locations
    for (const [sel, preservedEl] of preservedElements.entries()) {
      const newParent = existingSection.querySelector(sel)?.parentElement;
      if (newParent) {
        newParent.replaceChild(preservedEl, newParent.querySelector(sel));
      } else {
        console.warn(`[manualSectionRenderer] Could not restore preserved element for selector "${sel}"`);
      }
    }

    // 5. Reinitialize any scripts/components
    reinitializeScripts(existingSection);

    document.dispatchEvent(new CustomEvent('pf:section:updated'));

  } catch (err) {
    console.error(`[manualSectionRenderer] Failed to re-render section ${sectionId}:`, err);
  }
}

/**
 * Re-inserts script tags to re-run them.
 * @param {Element} root - The root element (usually the section) to search for scripts inside.
 */
function reinitializeScripts(root) {
  const scripts = root.querySelectorAll('script');

  scripts.forEach((oldScript) => {
    const newScript = document.createElement('script');

    if (oldScript.src) {
      newScript.src = oldScript.src;
    } else {
      newScript.textContent = oldScript.textContent;
    }

    for (const attr of oldScript.attributes) {
      newScript.setAttribute(attr.name, attr.value);
    }

    oldScript.replaceWith(newScript);
  });
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

    applyIndustryColorFix() {
    const boxes = document.querySelectorAll('.js-industry-box-trigger');

    boxes.forEach((el) => {
      const bg = el.getAttribute('data-bg');
      const color = el.getAttribute('data-color');

      if (bg) {
        el.style.background = bg;
        el.style.setProperty('background', bg, 'important');
      }

      if (color) {
        el.style.color = color;
        el.style.setProperty('color', color, 'important');
      }
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage = Theme.template.name.startsWith('product') &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }

    const giftCardPage = document.querySelector(".pf-prodetails-giftcard");
    const purrsonPage = document.querySelector(".pf-prodetails-purrson");

    const addonEl = document.querySelector(".pf-product-addons");

    const prodExcludedSelectors = [
      ".pf-product-accordion",
      ".pf-product-linklist",
      '.pf-prodetails-app-blocks',
      '.pf-popup-overlay',
      '.pf-icontext-row .pf-icontext-card:nth-child(2)',
      '.pf-icontext-row .pf-icontext-card:nth-child(1) .pf-icontext-icon',
      '.pf-icontext-row .pf-icontext-card:nth-child(1) .pf-icontext-title',
      '.pf-openbox-popup-content',

    ]

    if(addonEl) {
      prodExcludedSelectors.push(
        ".pf-addon-item:nth-child(1) .pf-addon-popup-content",
        ".pf-addon-item:nth-child(2) .pf-addon-popup-content",
        ".pf-addon-item:nth-child(3) .pf-addon-popup-content",
        ".pf-addon-item:nth-child(4) .pf-addon-popup-content",
        ".pf-addon-item:nth-child(5) .pf-addon-popup-content"
      );
    } else {
      prodExcludedSelectors.push(".pf-addon-list-wrapper");
    }

    const purrExcludedSelectors = [
      ".pf-product-accordion",
      ".pf-product-linklist",
      '.pf-prodetails-app-blocks',
      '.pf-icontext-row .pf-icontext-card:nth-child(2)',
      '.pf-icontext-row .pf-icontext-card:nth-child(1) .pf-icontext-icon',
      '.pf-icontext-row .pf-icontext-card:nth-child(1) .pf-icontext-title',
    ]

    // PF: Re-Render the section based on variant change
    if(!!giftCardPage) {
      renderSectionManually('pf-product-giftcard', '.pf-prodetails-wrapper', ['.pf-prodetails-app-blocks', ".pf-more-gift-idea"]);
    } else if(!!purrsonPage) {
      renderSectionManually('pf-purrson-details', '.pf-prodetails-wrapper', purrExcludedSelectors);
    } else {
      renderSectionManually('pf-product-details', '.pf-prodetails-wrapper', prodExcludedSelectors);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}





function saveIndustryColor() {
  // const key = 'industry_color_' + window.location.pathname;
  // localStorage.setItem(key, color);
  const el = document.querySelector('.js-industry-box-trigger');
  if (!el) return;

  const bg = el.getAttribute('data-bg');
  const color = el.getAttribute('data-color');

  if (bg && bg !== '') {
    localStorage.setItem('industry_bg', bg);
  }

  if (color && color !== '') {
    localStorage.setItem('industry_color', color);
  }
}

function applyIndustryColorFromStorage() {
  const el = document.querySelector('.js-industry-box-trigger');
  if (!el) return;

  const bg = el.getAttribute('data-bg') || localStorage.getItem('industry_bg');
  const color = el.getAttribute('data-color') || localStorage.getItem('industry_color');

  if (bg) el.style.setProperty('background', bg, 'important');
  if (color) el.style.setProperty('color', color, 'important');
}

document.addEventListener('pf:section:updated', function () {
  saveIndustryColor();
  applyIndustryColorFromStorage();

});
document.addEventListener('DOMContentLoaded', function () {
  saveIndustryColor();
  applyIndustryColorFromStorage();
});